import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, X, CheckCircle2, Circle, Clock, AlertTriangle, ChevronDown,
  Trash2, RotateCcw, Users, CalendarDays, Filter, ArrowUp, ArrowRight, ArrowDown
} from 'lucide-react';
import { api } from '../lib/api';
import { authService as auth } from '../lib/auth';

const SYSTEM_MODULES = [
  { id: 'sales_calling', name: 'Sales Calling' },
  { id: 'complaints', name: 'Complaints' },
  { id: 'class_delivery', name: 'Class Delivery' },
  { id: 'typing', name: 'Typing' },
  { id: 'content', name: 'Content' },
  { id: 'hr', name: 'HR' },
  { id: 'finance', name: 'Finance' },
  { id: 'offline_centre', name: 'Offline Centre' },
  { id: 'special_events', name: 'Special Events' },
  { id: 'task_manager', name: 'Task Manager' },
  { id: 'results', name: 'Results' },
];

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'text-red-600 bg-red-50 border-red-200', icon: ArrowUp },
  medium: { label: 'Medium', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: ArrowRight },
  low: { label: 'Low', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: ArrowDown },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-slate-600 bg-slate-50', icon: Circle },
  in_progress: { label: 'In Progress', color: 'text-blue-600 bg-blue-50', icon: Clock },
  done: { label: 'Done', color: 'text-green-600 bg-green-50', icon: CheckCircle2 },
};

const TaskManager = () => {
  const [activeView, setActiveView] = useState('today');
  const [tasks, setTasks] = useState([]);
  const [standupData, setStandupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const currentUser = auth.getCurrentUser();
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.role?.toLowerCase() === 'manager';

  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    title: '', description: '', priority: 'medium', due_date: today, category: '', assigned_to: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.getTaskUsers();
      setUsers(res.data || []);
    } catch (err) { console.error('Failed to load users'); }
  }, []);

  const fetchStandup = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTodayTasks();
      setStandupData(res.data);
    } catch (err) { toast.error('Failed to load standup board'); }
    finally { setLoading(false); }
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeView === 'my') params.my_tasks = true;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (dateFilter) params.due_date = dateFilter;
      const res = await api.listTasks(params);
      setTasks(res.data.tasks || []);
    } catch (err) { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, [activeView, statusFilter, categoryFilter, dateFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (activeView === 'today') fetchStandup();
    else fetchTasks();
  }, [activeView, fetchStandup, fetchTasks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createTask({
        ...formData,
        assigned_to: formData.assigned_to || null
      });
      toast.success('Task created');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', priority: 'medium', due_date: today, category: '', assigned_to: '' });
      if (activeView === 'today') fetchStandup(); else fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      if (newStatus === 'done') {
        await api.completeTask(taskId);
        toast.success('Task completed');
      } else {
        await api.updateTask(taskId, { status: newStatus });
        toast.success('Status updated');
      }
      if (activeView === 'today') fetchStandup(); else fetchTasks();
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleReopen = async (taskId) => {
    try {
      await api.reopenTask(taskId);
      toast.success('Task reopened');
      if (activeView === 'today') fetchStandup(); else fetchTasks();
    } catch (err) { toast.error('Failed to reopen'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.deleteTask(taskId);
      toast.success('Task deleted');
      if (activeView === 'today') fetchStandup(); else fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const TaskRow = ({ task }) => {
    const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
    const stat = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
    const PrioIcon = prio.icon;
    const StatIcon = stat.icon;
    const moduleName = SYSTEM_MODULES.find(m => m.id === task.category)?.name || task.category;

    return (
      <tr className="hover:bg-slate-50 group" data-testid={`task-row-${task.id}`}>
        <td className="px-4 py-3">
          <p className={`font-medium ${task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{task.title}</p>
          {task.description && <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${prio.color}`}>
            <PrioIcon size={12} /> {prio.label}
          </span>
        </td>
        <td className="px-4 py-3">
          <select
            value={task.status}
            onChange={(e) => e.target.value === 'done' ? handleStatusChange(task.id, 'done') : handleStatusChange(task.id, e.target.value)}
            className={`text-xs font-medium px-2 py-1 rounded-lg border-0 ${stat.color} cursor-pointer`}
            data-testid={`status-select-${task.id}`}
          >
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
          </select>
        </td>
        <td className="px-4 py-3 text-sm text-slate-600">{moduleName}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{task.assigned_to_name}</td>
        <td className="px-4 py-3 text-sm text-slate-500">
          {task.completed_at ? new Date(task.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            {task.status === 'done' && (
              <button onClick={() => handleReopen(task.id)} className="p-1 text-slate-400 hover:text-blue-600 rounded" title="Reopen">
                <RotateCcw size={15} />
              </button>
            )}
            <button onClick={() => handleDelete(task.id)} className="p-1 text-slate-400 hover:text-red-600 rounded" title="Delete">
              <Trash2 size={15} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="task-manager-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Task Manager</h1>
          <p className="text-slate-600">Daily tasks and morning meeting tracker</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
          data-testid="create-task-btn"
        >
          <Plus size={18} /> New Task
        </button>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="task-view-tabs">
        {[
          { id: 'today', label: "Today's Standup", icon: CalendarDays },
          { id: 'my', label: 'My Tasks', icon: Circle },
          { id: 'all', label: 'All Tasks', icon: Users },
        ].map(v => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition ${activeView === v.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${v.id}`}
          >
            <v.icon size={15} /> {v.label}
          </button>
        ))}
      </div>

      {/* TODAY'S STANDUP VIEW */}
      {activeView === 'today' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading standup board...</div>
          ) : standupData && standupData.users.length > 0 ? (
            <>
              {/* Summary Bar */}
              <div className="flex items-center gap-6 bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total Tasks</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="standup-total">{standupData.total_tasks}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Completed</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="standup-done">{standupData.total_done}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Remaining</p>
                  <p className="text-2xl font-bold text-amber-600">{standupData.total_tasks - standupData.total_done}</p>
                </div>
                <div className="ml-auto">
                  <div className="w-40 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${standupData.total_tasks > 0 ? (standupData.total_done / standupData.total_tasks * 100) : 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 text-right">
                    {standupData.total_tasks > 0 ? Math.round(standupData.total_done / standupData.total_tasks * 100) : 0}% done
                  </p>
                </div>
              </div>

              {/* Person-wise Cards */}
              {standupData.users.map(u => (
                <div key={u.user_id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`standup-user-${u.user_id}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold">
                        {u.user_name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-800">{u.user_name}</span>
                    </div>
                    <span className={`text-sm font-medium ${u.completed === u.total ? 'text-green-600' : 'text-slate-500'}`}>
                      {u.completed}/{u.total} done
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {u.tasks.map(t => <TaskRow key={t.id} task={t} />)}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <CalendarDays size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No tasks scheduled for today</p>
              <p className="text-sm text-slate-400 mt-1">Click "New Task" to add tasks for today's meeting</p>
            </div>
          )}
        </div>
      )}

      {/* MY TASKS / ALL TASKS VIEW */}
      {(activeView === 'my' || activeView === 'all') && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex flex-wrap gap-3">
              <input
                type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-date"
              />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-status">
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-category">
                <option value="">All Categories</option>
                {SYSTEM_MODULES.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              {(dateFilter || statusFilter || categoryFilter) && (
                <button onClick={() => { setDateFilter(''); setStatusFilter(''); setCategoryFilter(''); }}
                  className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Tasks Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : tasks.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <Circle size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="font-medium">No tasks found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Task</th>
                      <th className="px-4 py-3 text-left font-medium">Priority</th>
                      <th className="px-4 py-3 text-left font-medium">Status</th>
                      <th className="px-4 py-3 text-left font-medium">Category</th>
                      <th className="px-4 py-3 text-left font-medium">Assigned To</th>
                      <th className="px-4 py-3 text-left font-medium">Completed At</th>
                      <th className="px-4 py-3 text-left font-medium w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tasks.map(t => <TaskRow key={t.id} task={t} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE TASK MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">New Task</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task Title *</label>
                <input type="text" value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" required data-testid="task-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" rows={2} data-testid="task-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority *</label>
                  <select value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" data-testid="task-priority">
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Due Date *</label>
                  <input type="date" value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" required data-testid="task-due-date" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category / Module *</label>
                <select value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" required data-testid="task-category">
                  <option value="">Select Module</option>
                  {SYSTEM_MODULES.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Assign To</label>
                <select value={formData.assigned_to}
                  onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" data-testid="task-assign-to">
                  <option value="">Myself</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700" data-testid="task-submit">Create Task</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManager;
